import {Dispatch, SetStateAction, useLayoutEffect} from 'react';
import {useNodesInitialized} from 'reactflow';

import {unhideFlowchartElementAfterLayout} from 'ui-library/reactflow/layout/hideFlowchartElementBeforeLayoutUtils';

import {FALLBACK_COLUMN_NODE_HEIGHT} from './ProcedureRunDesignFlowchartColumnNode';
import {
  computeColumnTotalHeight,
  recomputeColumnPositions,
  recomputeGridPositions,
} from './ProcedureRunDesignFlowchartLayoutUtils';
import type {ProcedureRunDesignFlowchartLayoutEdge} from './useProcedureRunDesignFlowchartEdges';
import type {
  ProcedureRunDesignFlowchartLayoutNode,
  ProcedureRunDesignFlowchartLayoutOptions,
} from './useProcedureRunDesignFlowchartLayout';

interface UseProcedureRunDesignFlowchartMeasuredLayoutArgs {
  initialColumnNodes: Array<ProcedureRunDesignFlowchartLayoutNode>;
  initialRows: Array<Array<ProcedureRunDesignFlowchartLayoutNode>>;
  layoutOptions: ProcedureRunDesignFlowchartLayoutOptions;
  nodes: Array<ProcedureRunDesignFlowchartLayoutNode>;
  setNodes: Dispatch<SetStateAction<Array<ProcedureRunDesignFlowchartLayoutNode>>>;
  setEdges: Dispatch<SetStateAction<Array<ProcedureRunDesignFlowchartLayoutEdge>>>;
}

/**
 * Drives the second phase of the two-phase layout: once ReactFlow has measured every expected
 * node, recompute grid positions and unhide the nodes. Until then the current nodes are left
 * untouched so the visible flowchart isn't disturbed.
 */
export function useProcedureRunDesignFlowchartMeasuredLayout({
  initialColumnNodes,
  initialRows,
  layoutOptions,
  nodes,
  setNodes,
  setEdges,
}: UseProcedureRunDesignFlowchartMeasuredLayoutArgs): void {
  const nodesInitialized = useNodesInitialized();

  // Hash id + dimensions only — position-only changes produce the same hash,
  // preventing setNodes -> hash-change -> re-layout loops
  const nodeLayoutDepsHash = getNodeLayoutDepsHash(nodes);

  // Lay out only once every expected node has been measured. Computed in the body (not the effect)
  // as a primitive boolean so the effect can depend on it without depending on `nodes`. Until then,
  // leave the current nodes/edges untouched so the visible flowchart isn't disturbed.
  const hasLayout = haveAllNodesBeenMeasured(nodes, initialColumnNodes, initialRows);

  useLayoutEffect(() => {
    if (!nodesInitialized || !hasLayout) {
      return;
    }

    setNodes((currentNodes) => {
      const currentMeasuredById = new Map(currentNodes.map((currentNode) => [currentNode.id, currentNode]));
      const recomputedNodes = recomputeNodePositions(
        initialRows,
        initialColumnNodes,
        currentMeasuredById,
        layoutOptions
      );
      return recomputedNodes.map((node) => unhideFlowchartElementAfterLayout(node));
    });

    // Reveal edges in the same pass so arrows appear atomically with the nodes they connect.
    setEdges((currentEdges) => currentEdges.map((edge) => unhideFlowchartElementAfterLayout(edge)));
    // nodeLayoutDepsHash captures dimension changes without including positions, so layout only
    // re-runs when sizes change, not when setNodes updates positions. layoutOptions is expected
    // to be a stable module-level constant at the call site.
  }, [
    nodesInitialized,
    hasLayout,
    nodeLayoutDepsHash,
    initialRows,
    initialColumnNodes,
    setNodes,
    setEdges,
    layoutOptions,
  ]);
}

/** Whether ReactFlow has measured (width and height) every column and grid node we expect. */
function haveAllNodesBeenMeasured(
  nodes: Array<ProcedureRunDesignFlowchartLayoutNode>,
  initialColumnNodes: Array<ProcedureRunDesignFlowchartLayoutNode>,
  initialRows: Array<Array<ProcedureRunDesignFlowchartLayoutNode>>
): boolean {
  const expectedIds = new Set([
    ...initialColumnNodes.map((node) => node.id),
    ...initialRows.flat().map((node) => node.id),
  ]);
  const measuredNodesById = new Map(nodes.map((node) => [node.id, node]));
  return [...expectedIds].every((id) => {
    const measuredNode = measuredNodesById.get(id);
    return measuredNode != null && (measuredNode.width ?? 0) > 0 && (measuredNode.height ?? 0) > 0;
  });
}

function getNodeLayoutDepsHash(nodes: Array<ProcedureRunDesignFlowchartLayoutNode>): string {
  return JSON.stringify(nodes.map(({id, height, width}) => ({id, height, width})));
}

/**
 * Recomputes the positions of the nodes in the flowchart.
 * This function is called when all expected nodes are measured.
 * It recomputes the positions of the nodes in the flowchart and returns the recomputed nodes.
 */
function recomputeNodePositions(
  initialRows: Array<Array<ProcedureRunDesignFlowchartLayoutNode>>,
  initialColumnNodes: Array<ProcedureRunDesignFlowchartLayoutNode>,
  measuredNodesById: Map<string, ProcedureRunDesignFlowchartLayoutNode>,
  layoutOptions: ProcedureRunDesignFlowchartLayoutOptions
): Array<ProcedureRunDesignFlowchartLayoutNode> {
  const measuredColumnNodes = initialColumnNodes.map(
    (columnNode) => measuredNodesById.get(columnNode.id) ?? columnNode
  );

  const measuredRows = initialRows.map((row) =>
    row.map((rowNode) => measuredNodesById.get(rowNode.id) ?? rowNode)
  );

  // Grid rows start below the column headers.
  // Column nodes are full-height overlays after layout, so their measured height is totalHeight,
  // not the header content height. Use the fixed header height constant instead.
  const columnHeaderHeight = FALLBACK_COLUMN_NODE_HEIGHT;

  const {repositionedRows, columnDimensions} = recomputeGridPositions(measuredRows, {
    horizontalGap: layoutOptions.horizontalGap,
    verticalGap: layoutOptions.verticalGap,
    startX: layoutOptions.startX,
    startY: layoutOptions.startY + columnHeaderHeight + layoutOptions.columnHeaderBottomGap,
  });

  const columnTotalHeight = computeColumnTotalHeight(repositionedRows, layoutOptions.startY);
  const repositionedColumnNodes = recomputeColumnPositions(measuredColumnNodes, columnDimensions, {
    startY: layoutOptions.startY,
    totalHeight: columnTotalHeight,
  });

  return [...repositionedColumnNodes, ...repositionedRows.flat()];
}
